import { OverviewCard } from '@ctw/shared/components/containers/overview-card';
import { RenderSyncedWithRecordIcon } from '@ctw/shared/content/resource/resource-title-column';
import { tw } from '@ctw/shared/utils/tailwind';
import { faHeartPulse } from '@fortawesome/pro-solid-svg-icons';
import type { ReactNode } from 'react';
import type { OverviewVital } from './use-patient-vitals-overview-data';

export interface PatientVitalsOverviewCardProps {
  data: Array<OverviewVital>;
  isLoading: boolean;
  emptyState: ReactNode;
  footerCTA?: { label: string; onClick: () => void };
}

/**
 * Presentational vitals overview card. Renders the pre-computed vital buckets it
 * is handed; the transform lives in `usePatientVitalsOverviewData`.
 */
export const PatientVitalsOverviewCard = ({
  data,
  isLoading,
  emptyState,
  footerCTA,
}: PatientVitalsOverviewCardProps) => (
  <OverviewCard
    headerIcon={faHeartPulse}
    loading={isLoading}
    title="Most Recent Vitals"
    helpText="Displays most recent available recorded vitals"
    emptyState={emptyState}
    empty={data.length === 0}
    footerCTA={footerCTA}
    testId="patient-vitals-overview"
    fullbleed={true}
  >
    {data.map((vital) => (
      <div
        className={tw`flex space-x-1.5 px-2 py-1.5`}
        data-testid="overview-row"
        key={vital.vital}
      >
        <div className={tw`flex justify-between`}>
          <div className={tw`w-[1.5rem] min-w-[1.5rem] max-w-[1.5rem]`}>
            {RenderSyncedWithRecordIcon(vital.firstParty ?? false)}
          </div>
          <div className={tw`w-14 font-medium`}>{vital.vital}</div>
        </div>
        <div className={tw`w-[7.25rem] text-right`}>{vital.value}</div>
        <div className={tw`w-[5.25rem] text-right`}>
          {vital.date ? vital.date : <span className={tw`text-content-icon`}>N/A</span>}
        </div>
      </div>
    ))}
  </OverviewCard>
);
